/**
 * The courseware player — where someone actually learns.
 *
 * Three things share this screen, and keeping them separate is what makes it work:
 *
 *   THE OUTLINE is ours.     Sections, subsections and units with their completion state,
 *                            drawn in our own chrome from one platform endpoint.
 *   THE UNIT is the platform's. Rendered by Open edX in a frame, because a course can
 *                            contain any XBlock ever installed — including ones we have
 *                            never heard of — and every one of them ships its own view,
 *                            CSS and JavaScript. See `unitFrameUrl` in courseware.ts.
 *   THE JOURNEY is ours.     Previous/Next across the whole course, and resume.
 *
 * Access is decided BEFORE any of that, by `/api/course_home/course_metadata/{id}` — the
 * same call the course page makes. It answers for anonymous visitors too, and its
 * `course_access` block explains a closed course in the platform's own words. The outline
 * endpoint 403s a learner who is not enrolled, so gating on the metadata first is what
 * turns "not enrolled" from a failed request into a screen that tells them where to go.
 *
 * Layout is the prototype's `#screen-unit`: a two-column player, content left on the page
 * ground, a white outline rail on the right that scrolls with its own overflow.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Banner, Icon, Spinner } from '../../shared/ui';
import { ApiError } from '../../shared/lib/openedx';
import {
  courseHref,
  formatDate,
  getCourseMetadata,
  visibleToTenant,
  type CourseMetadata,
} from '../../shared/lib/catalogue';
import {
  PLATFORM_FRAME_SANDBOX,
  frameOrigin,
  getCourseOutline,
  getCoursewareCourse,
  getSequenceMetadata,
  hasEnded,
  markBlocksRead,
  neighbours,
  progressPercent,
  resizeHeight,
  resumeUnitId,
  unitFrameUrl,
  unitIcon,
  viewCompletableBlocks,
  type CourseOutline,
  type OutlineUnit,
  type SequenceMetadata,
} from '../../shared/lib/courseware';
import { AiPanel } from '../../shared/ai/AiPanel';
import { CourseCertificateSection } from './CourseCertificate';
import './unit.css';

export default function Unit() {
  const { courseId = '', unitId } = useParams();
  const queryClient = useQueryClient();

  // May I be here? Everything below is gated on this, so it is the only query that runs
  // unconditionally.
  const metadata = useQuery({
    queryKey: ['course-metadata', courseId],
    queryFn: () => getCourseMetadata(courseId),
    retry: false,
  });

  // The detail endpoints answer for ANY course from ANY tenant's hostname (see the header
  // note in catalogue.ts), so a course id from another academy has to be refused here.
  //
  // `visibleToTenant`, not `belongsToTenant`: a course can reach this academy two ways, by
  // its org or by a per-course licence, and the org-only test would let a learner enrol in a
  // licensed course from the catalogue and then be refused the player for it.
  const ours = metadata.data ? visibleToTenant(courseId, metadata.data.org) : true;
  const allowed = metadata.data?.course_access.has_access === true && ours;

  const outline = useQuery({
    queryKey: ['course-outline', courseId],
    queryFn: () => getCourseOutline(courseId),
    enabled: allowed,
    retry: false,
  });

  const course = useQuery({
    queryKey: ['courseware-course', courseId],
    queryFn: () => getCoursewareCourse(courseId),
    enabled: allowed,
    retry: false,
  });

  /**
   * Refresh completion when the learner moves on.
   *
   * The platform records completion inside the frame — an HTML block completes on view, a
   * video on watch — so our copy of the outline is stale the moment someone finishes a
   * unit. Refetching as they leave is the built-in way to catch up: the per-block
   * completion handler the official player calls lives under `/courses/…/xblock/…/handler/`,
   * which is not a path this app can reach (see the report note), and the outline endpoint
   * already carries the same truth.
   */
  useEffect(
    () => () => {
      void queryClient.invalidateQueries({ queryKey: ['course-outline', courseId] });
      // FINISHING THE LAST UNIT CAN ISSUE TWO CREDENTIALS, both written server-side inside
      // the request that recorded the completion: this course's certificate, and — if this
      // course was the last unfinished step of a learning path — that pathway's. Neither
      // is visible until the queries holding them are dropped, so leaving a unit drops
      // both. Cheap: they refetch only when a page that reads them is mounted.
      void queryClient.invalidateQueries({ queryKey: ['my-certificates'] });
      void queryClient.invalidateQueries({ queryKey: ['my-courses'] });
      void queryClient.invalidateQueries({ queryKey: ['pathway-certificate'] });
      void queryClient.invalidateQueries({ queryKey: ['my-pathway-certificates'] });
    },
    [unitId, courseId, queryClient],
  );

  if (metadata.isPending) {
    return (
      <main className="player player--message">
        <Spinner label="Opening this course…" />
      </main>
    );
  }

  if (metadata.isError) return <PlayerProblem error={metadata.error} courseId={courseId} />;
  if (!ours) return <PlayerProblem error={null} courseId={courseId} notOurs />;
  if (!allowed) return <NoAccess courseId={courseId} metadata={metadata.data} />;

  if (outline.isPending) {
    return (
      <main className="player player--message">
        <Spinner label="Loading the course outline…" />
      </main>
    );
  }

  if (outline.isError) return <PlayerProblem error={outline.error} courseId={courseId} />;

  if (outline.data.units.length === 0) {
    return (
      <main className="player player--message">
        <h1>
          {outline.data.scheduled
            ? 'This course opens in stages'
            : 'There is nothing to learn here yet'}
        </h1>
        <p>
          {outline.data.scheduled ? (
            <>
              The course team has scheduled the content of{' '}
              <strong>{outline.data.title}</strong> to open later, so there is nothing to
              read yet. Your place is safe — check the course page for the dates.
            </>
          ) : (
            <>
              The course team has not published any units in{' '}
              <strong>{outline.data.title}</strong>. You are enrolled, so it will appear
              here as soon as they do.
            </>
          )}
        </p>
        <p>
          <Link to={courseHref(courseId)} className="ui-btn" data-variant="primary">
            Back to the course page
          </Link>
        </p>
      </main>
    );
  }

  // No unit in the url: this is "take me back to where I was".
  if (!unitId) return <Resume courseId={courseId} outline={outline.data} />;

  const unit = outline.data.units.find((candidate) => candidate.id === unitId);
  if (!unit) {
    return (
      <main className="player player--message">
        <h1>We could not find that unit</h1>
        <p>
          It is not part of <strong>{outline.data.title}</strong> any more. The course team
          may have moved or removed it since this link was made.
        </p>
        <p>
          <Link
            to={`/learn/${encodeURIComponent(courseId)}`}
            className="ui-btn"
            data-variant="primary"
          >
            Go to where you left off
          </Link>
        </p>
      </main>
    );
  }

  return (
    <Player
      courseId={courseId}
      unit={unit}
      outline={outline.data}
      ended={hasEnded(course.data)}
      staffOnly={metadata.data.is_staff && !metadata.data.is_enrolled}
    />
  );
}

/* ------------------------------------------------------------------ resume ----- */

/**
 * Send the learner to the unit they left off at.
 *
 * A `replace` navigation on purpose: the bare course url is a redirect, not a place, and
 * leaving it in history means Back takes you to a page that immediately bounces you
 * forward again.
 */
function Resume({ courseId, outline }: { courseId: string; outline: CourseOutline }) {
  const target = useQuery({
    queryKey: ['course-resume', courseId],
    queryFn: () => resumeUnitId(courseId, outline),
    // Asked once per visit. Cached, it would send someone back to yesterday's unit.
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });

  if (target.isPending) {
    return (
      <main className="player player--message">
        <Spinner label="Finding where you left off…" />
      </main>
    );
  }

  const unitId = target.data ?? outline.units[0]?.id;
  if (!unitId) return <Navigate to={courseHref(courseId)} replace />;

  return (
    <Navigate
      to={`/learn/${encodeURIComponent(courseId)}/${encodeURIComponent(unitId)}`}
      replace
    />
  );
}

/* ------------------------------------------------------------------ player ----- */

function Player({
  courseId,
  unit,
  outline,
  ended,
  staffOnly,
}: {
  courseId: string;
  unit: OutlineUnit;
  outline: CourseOutline;
  ended: boolean;
  staffOnly: boolean;
}) {
  const { previous, next } = neighbours(outline, unit.id);

  // One subsection's own rules — gating, exams, grading format. Fetched for the subsection
  // being viewed only; the outline covers everything else.
  const sequence = useQuery({
    queryKey: ['course-sequence', unit.subsectionId],
    queryFn: () => getSequenceMetadata(unit.subsectionId),
    retry: false,
  });

  const unitHref = (target: OutlineUnit) =>
    `/learn/${encodeURIComponent(courseId)}/${encodeURIComponent(target.id)}`;

  return (
    <main className="player">
      <div className="player__main">
        <div className="player__top">
          <Link to={courseHref(courseId)} className="ui-btn" data-variant="ghost">
            <Icon name="arrow_back" />
            Back to course
          </Link>
        </div>

        <p className="player__crumb">
          <Link to={courseHref(courseId)}>{outline.title}</Link>
          <span aria-hidden="true"> / </span>
          {unit.sectionTitle}
          <span aria-hidden="true"> / </span>
          {unit.subsectionTitle}
        </p>

        <div className="player__title-row">
          <div className="player__title">
            <h2>{unit.title}</h2>
            <p className="module-meta">
              {unit.subsectionTitle} · Unit {unit.position} of {unit.positionOf}
              {unit.complete && (
                <>
                  {' · '}
                  <span className="player__done">
                    <Icon name="check_circle" />
                    Completed
                  </span>
                </>
              )}
            </p>
          </div>
        </div>

        {staffOnly && (
          <Banner tone="info">
            You are seeing this as a member of the course team. You are not enrolled, so
            your progress here is not a learner&rsquo;s progress.
          </Banner>
        )}

        {ended && (
          <Banner tone="info">
            This course has ended. You can still read everything in it, but graded work is
            no longer collected.
          </Banner>
        )}

        {sequence.data?.banner_text && <Banner tone="info">{sequence.data.banner_text}</Banner>}

        <UnitPane
          unit={unit}
          courseId={courseId}
          sequence={sequence.data}
          sequenceFailed={sequence.isError}
        />

        {/*
          * AI, if this academy has any. Renders nothing at all when no scope row matches the
          * slot below, so a tenant without AI cannot tell this is here.
          *
          * BELOW the platform's render and OUTSIDE its card, deliberately. Machine-written
          * text has to be visibly distinguishable from course material — the provenance line
          * inside the panel says so in words, and the panel's own chrome has to agree, which
          * it cannot do while sharing the unit's card.
          *
          * The slot string is the contract: it must equal an `AIWorkflowScope`
          * `ui_slot_selector_id` row, because an unmatched slot resolves to nothing rather
          * than to a default. `locationId` is the vertical's usage key, which the server
          * validates as an opaque UsageKey and the workflow uses to fetch this unit's content
          * — passed from the route's own data rather than scraped back out of the URL, which
          * is what upstream's component has to do.
          */}
        <AiPanel slot="thinlms-unit-summary" courseId={courseId} locationId={unit.id} />

        {/*
          THE END OF THE COURSE — and only there.
          Gated on `!next`, which is exactly "this is the last unit in the whole course",
          not the last unit of a subsection. A learner who has just run out of content is
          the one person who wants to know where their certificate is; showing this on every
          unit would be an interruption, and showing it in a floating panel would be worse.

          It renders nothing at all for a course that awards no certificate, so the last
          unit of such a course looks exactly as it did before.
        */}
        {!next && (
          <CourseCertificateSection
            courseId={courseId}
            /* A member of the course team viewing without an enrolment earns nothing here,
               and the banner above already says so. */
            enrolled={!staffOnly}
            variant="compact"
          />
        )}

        <nav className="player__nav" aria-label="Course navigation">
          {previous ? (
            <Link to={unitHref(previous)} className="ui-btn" data-variant="ghost">
              <Icon name="arrow_back" />
              <span className="player__nav-label">
                <small>Previous</small>
                {previous.title}
              </span>
            </Link>
          ) : (
            <span className="player__nav-end">You are at the start of this course</span>
          )}

          {next ? (
            <Link to={unitHref(next)} className="ui-btn" data-variant="primary">
              <span className="player__nav-label">
                <small>Next</small>
                {next.title}
              </span>
              <Icon name="arrow_forward" />
            </Link>
          ) : (
            <Link to={courseHref(courseId)} className="ui-btn" data-variant="primary">
              Finish and go back to the course
              <Icon name="arrow_forward" />
            </Link>
          )}
        </nav>
      </div>

      <Sidebar courseId={courseId} outline={outline} current={unit} />
    </main>
  );
}

/* -------------------------------------------------------------- the content ---- */

/** Height bounds, so a bad measurement cannot collapse the frame or blow up the page. */
const MIN_FRAME = 260;
const MAX_FRAME = 8000;

/**
 * The unit itself, rendered by Open edX.
 *
 * Sizing is the fiddly part, and it is done twice on purpose:
 *
 *   1. `plugin.resize` — the platform's own protocol, posted by
 *      `courseware-chromeless.html` on load, resize and DOM mutation. It is the right
 *      answer and it works cross-origin, but the template only installs the reporter when
 *      the request's `Referer` matches the Site's `LEARNING_MICROFRONTEND_URL`. Verified in
 *      the template source: the whole script sits inside `% if is_learning_mfe:`.
 *
 *   2. Measuring the frame's own document, when it is same-origin. Not a hack — the same
 *      technique the authoring preview uses — and it is what keeps the player honest on an
 *      install that has not pointed that setting at this app.
 *
 * If neither works the frame keeps a generous fixed height and scrolls internally. That is
 * a worse experience than a fitted frame, but it is a working one, which a collapsed
 * 0-pixel frame is not.
 */
function UnitPane({
  unit,
  courseId,
  sequence,
  sequenceFailed,
}: {
  unit: OutlineUnit;
  courseId: string;
  sequence: SequenceMetadata | undefined;
  sequenceFailed: boolean;
}) {
  const queryClient = useQueryClient();
  const src = useMemo(() => unitFrameUrl(unit.id, sequence?.format), [unit.id, sequence?.format]);
  const origin = useMemo(() => frameOrigin(src), [src]);
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState<number | null>(null);
  const [rendered, setRendered] = useState<boolean | null>(null);

  // A new unit is a new document. Without this the previous unit's height is kept while
  // the next one loads, so a short unit inherits a tall frame full of empty page.
  useEffect(() => {
    setHeight(null);
    setRendered(null);
  }, [src]);

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      // Only from the origin we asked for, and only from OUR frame: any other embed on the
      // page can post a lookalike message, and resizing to its height would be wrong.
      if (event.origin !== origin) return;
      if (!frame.current || event.source !== frame.current.contentWindow) return;

      const next = resizeHeight(event.data);
      if (next !== null) setHeight(Math.min(Math.max(next, MIN_FRAME), MAX_FRAME));
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [origin]);

  /**
   * Follow the frame's own document, when we are allowed to read it.
   *
   * `contentDocument` is null across origins — that is the browser refusing, not an error
   * to report — so this simply does nothing in that case and the message protocol is left
   * to do the work.
   */
  useEffect(() => {
    const doc = frame.current?.contentDocument;
    // `#content` is the platform's own content wrapper, and it is the element the
    // platform's own resize reporter measures. Measuring the DOCUMENT instead sets up a
    // feedback loop that runs the frame to its ceiling: the page's html/body fill whatever
    // height the frame has, so every measurement is at least as tall as the last one it
    // caused. Observed live — the frame settled at the 8000px cap on every unit.
    // No fallback to `body` on purpose: a page without `#content` is not the platform's
    // chromeless render, and measuring its body is the same feedback loop by another name.
    // Better to keep the stylesheet's height than to inflate the frame to its ceiling.
    const content = doc?.getElementById('content');
    if (!content) return;

    const follow = () => {
      const measured = content.getBoundingClientRect().height;
      if (measured > 0) setHeight(Math.min(Math.max(measured, MIN_FRAME), MAX_FRAME));
    };
    follow();

    // Blocks finish rendering after load — a video builds its player, a drag-and-drop its
    // canvas — so a single measurement at load time is always too small for something.
    const observer = new ResizeObserver(follow);
    observer.observe(content);
    return () => observer.disconnect();
  }, [rendered]);

  /**
   * TELL THE FRAME WHAT IS ON SCREEN. This is what records a text unit as read.
   *
   * The platform marks a completable block complete once it has been VISIBLE for a delay,
   * and its tracker cannot see our scroll position, so it asks us. From
   * `lms/static/completion/js/ViewedEvent.js`:
   *
   *     if (window === window.parent) { this.registerDomHandlers(); }   // its own scroll
   *     else { window.addEventListener('message', this.handleVisibilityMessage); }
   *
   * In an iframe it takes the second branch and then waits for a `unit.visibilityStatus`
   * message — which only the official learning frontend sends. Without it the tracker is
   * installed, armed, and never evaluates anything.
   *
   * That was the whole of "progress is not working". Measured before this: a text unit read
   * for 20 seconds produced zero BlockCompletion rows and zero `publish_completion` calls,
   * while video and problems completed normally — because a video publishes its own
   * completion and a problem publishes on submit. Only view-based completion goes through
   * this message, and view-based completion is most of a course.
   *
   * The shape is the platform's, not ours: `topPosition` is the frame's offset in OUR
   * viewport and `viewportHeight` is our viewport, which is what lets the tracker convert a
   * block's frame-relative rect into a screen position. Sent on load and, throttled, on
   * scroll and resize — the same three triggers the official player uses.
   */
  useEffect(() => {
    if (!rendered) return;

    let last = 0;
    let timer: number | null = null;

    const post = () => {
      const element = frame.current;
      const target = element?.contentWindow;
      if (!element || !target) return;
      const rect = element.getBoundingClientRect();
      target.postMessage(
        {
          type: 'unit.visibilityStatus',
          data: { topPosition: rect.top, viewportHeight: window.innerHeight },
        },
        // Addressed to the frame's own origin rather than '*': the message is harmless, but
        // a wildcard target would hand it to whatever happens to be loaded there.
        origin,
      );
    };

    // Throttled to 100ms, as upstream does — the tracker re-evaluates every element on each
    // message, and a raw scroll listener would run that on every frame.
    const throttled = () => {
      const now = Date.now();
      if (now - last >= 100) {
        last = now;
        post();
      } else if (timer === null) {
        timer = window.setTimeout(() => {
          timer = null;
          last = Date.now();
          post();
        }, 100);
      }
    };

    post();
    window.addEventListener('scroll', throttled, { passive: true });
    window.addEventListener('resize', throttled);
    return () => {
      if (timer !== null) window.clearTimeout(timer);
      window.removeEventListener('scroll', throttled);
      window.removeEventListener('resize', throttled);
    };
    // `height` is a dependency because the frame moves when it resizes, and a unit that
    // grows after load would otherwise keep reporting its old position.
  }, [rendered, origin, height]);

  /**
   * MOVING ON COUNTS AS READING.
   *
   * The platform will only tick a text block once its top AND bottom have been on screen (see
   * `markBlocksRead`), so a unit taller than the window stays unticked for a learner who reads
   * what they need and presses Next — which is what "the progress does not work" looked like on
   * a long unit, while short ones ticked by themselves.
   *
   * The block ids are captured while the unit is on screen and used in the cleanup, because by
   * then the frame is being torn down and its document is no longer readable. React runs this
   * cleanup before the next unit's effects, so `reading.current` still holds the unit being
   * left, not the one arriving.
   *
   * Only blocks the platform itself marked as view-completable are sent, so nothing that has to
   * be EARNED — a problem, a video, an open response — is ever completed by navigating.
   */
  const reading = useRef<string[]>([]);

  useEffect(() => {
    if (!rendered) return;
    reading.current = viewCompletableBlocks(frame.current);
  }, [rendered]);

  useEffect(
    () => () => {
      const blocks = reading.current;
      reading.current = [];
      // Refresh the outline AFTER the writes land, not before. The parent also invalidates on
      // navigation, and that fires first — so without this the sidebar refetches the old
      // completion state and the unit the learner just left stays unticked until something
      // else happens to refetch.
      void markBlocksRead(courseId, blocks).then(() =>
        queryClient.invalidateQueries({ queryKey: ['course-outline', courseId] }),
      );
    },
    [src, courseId, queryClient],
  );

  /**
   * Did the platform actually render a block?
   *
   * Same-origin only, and worth doing: a misrouted frame answers 200 with a login page or
   * an error page, which looks like a blank unit rather than a broken one. Every rendered
   * XBlock carries `data-block-type`, so its absence is a definite answer. Across origins
   * we cannot look, and assume it worked rather than accusing the platform of failing.
   */
  function onLoad() {
    const doc = frame.current?.contentDocument;
    if (!doc) {
      setRendered(true);
      return;
    }
    setRendered(Boolean(doc.querySelector('[data-block-type]')));
  }

  if (sequence?.gated_content.gated) {
    return (
      <section className="content-panel is-active content-panel--locked">
        <Icon name="lock" />
        <h3>This part of the course is locked</h3>
        <p>
          {sequence.gated_content.prereq_section_name
            ? `Finish “${sequence.gated_content.prereq_section_name}” first — the course team set it as a prerequisite for this section.`
            : 'The course team set a prerequisite for this section. Complete it first and this will unlock.'}
        </p>
      </section>
    );
  }

  if (sequence?.is_time_limited || sequence?.is_proctored) {
    return (
      <section className="content-panel is-active content-panel--locked">
        <Icon name="timer" />
        <h3>This is a timed exam</h3>
        <p>
          Timed and proctored exams are not available in this player yet. Contact the course
          team — nothing has been started, so your attempt is untouched.
        </p>
      </section>
    );
  }

  return (
    <section className="content-panel is-active">
      {sequenceFailed && (
        <Banner tone="warning">
          <strong>We could not check this section&rsquo;s rules.</strong> The unit below is
          shown as the platform rendered it; if it is graded work, reload before you answer.
        </Banner>
      )}

      {rendered === false && (
        <Banner tone="danger">
          <strong>The academy did not return this unit.</strong> Its content could not be
          rendered — reload the page, and tell the course team if it keeps happening.
        </Banner>
      )}

      <iframe
        ref={frame}
        className="unit-frame"
        title={unit.title}
        src={src}
        onLoad={onLoad}
        style={height ? { height: `${height}px` } : undefined}
        // The platform's own allow-list, verbatim from the official player: blocks include
        // video, audio recording and LTI tools that need these to work at all.
        allow="microphone *; camera *; midi *; encrypted-media *; clipboard-write *; autoplay *"
        // WHY THIS FRAME IS SANDBOXED — see `PLATFORM_FRAME_SANDBOX` for the measurement.
        // Short version: a framed Open edX error page busts out of its frame and replaces
        // THIS page, so one component's failure erased four working ones. Withholding
        // `allow-top-navigation` keeps a broken block's error inside the block.
        sandbox={PLATFORM_FRAME_SANDBOX}
        allowFullScreen
        scrolling="no"
        referrerPolicy="origin"
      />
    </section>
  );
}

/* ----------------------------------------------------------------- sidebar ----- */

function Sidebar({
  courseId,
  outline,
  current,
}: {
  courseId: string;
  outline: CourseOutline;
  current: OutlineUnit;
}) {
  const percent = progressPercent(outline.done, outline.total);

  return (
    <aside className="player__side" aria-label="Course outline">
      <div className="player__side-head">
        <h4>{outline.title}</h4>
        <p>
          {outline.done} of {outline.total} units done · {percent}% complete
        </p>
        <div className="progress">
          <div className="progress-bar" style={{ width: `${percent}%` }} />
        </div>
      </div>

      {outline.sections.map((section) => (
        <div className="side-section" key={section.id}>
          <h5 className="side-section__head">{section.title}</h5>

          {section.subsections.length === 0 && (
            <p className="side-section__empty">Nothing published in this section yet.</p>
          )}

          {section.subsections.map((subsection) => (
            <div className="side-module" key={subsection.id}>
              <div className="side-module__head">
                {subsection.title}
                <span className="count">
                  {subsection.units.length === 0
                    ? 'empty'
                    : `${subsection.done}/${subsection.units.length}`}
                </span>
              </div>

              {subsection.units.map((unit) => {
                const icon = unitIcon(unit.icon);
                const state = [
                  unit.complete ? 'is-done' : '',
                  unit.id === current.id ? 'is-active' : '',
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <Link
                    key={unit.id}
                    to={`/learn/${encodeURIComponent(courseId)}/${encodeURIComponent(unit.id)}`}
                    className={`side-unit ${state}`}
                    aria-current={unit.id === current.id ? 'page' : undefined}
                  >
                    <span className={`type-icon ${icon.tone}`}>
                      <Icon name={unit.complete ? 'check' : icon.name} />
                    </span>
                    <span className="side-unit__label">{unit.title}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </div>
      ))}
    </aside>
  );
}

/* ------------------------------------------------------------- closed doors ---- */

/**
 * Why the courseware will not open, in the platform's own words where it has them.
 *
 * `course_access.user_message` is already written for a learner, so it is passed through
 * rather than paraphrased — paraphrasing means keeping a second copy of every access rule
 * the platform has. What is added is the thing the platform cannot know: what to do next
 * in THIS app.
 */
function NoAccess({ courseId, metadata }: { courseId: string; metadata: CourseMetadata }) {
  const { error_code: code, user_message: message } = metadata.course_access;

  const notEnrolled = code === 'enrollment_required';
  const notSignedIn = code === 'authentication_required';
  const notStarted = code === 'course_not_started';

  return (
    <main className="player player--message">
      <h1>
        {notEnrolled
          ? 'You have not joined this course yet'
          : notStarted
            ? 'This course has not started'
            : notSignedIn
              ? 'Sign in to open this course'
              : 'This course is not open to you'}
      </h1>

      <p>
        {notEnrolled ? (
          <>
            Enrol in <strong>{metadata.title}</strong> and you can start straight away — it
            takes one click from the course page.
          </>
        ) : notStarted ? (
          <>
            <strong>{metadata.title}</strong> opens
            {metadata.start ? ` on ${formatDate(metadata.start)}` : ' at a date the course team has not announced yet'}.
            Your place is already booked; come back then.
          </>
        ) : (
          (message ?? 'The academy did not say why. The course team can tell you more.')
        )}
      </p>

      <p>
        {notSignedIn ? (
          <Link
            to={`/login?next=${encodeURIComponent(`/learn/${courseId}`)}`}
            className="ui-btn"
            data-variant="primary"
          >
            Sign in
          </Link>
        ) : (
          <Link to={courseHref(courseId)} className="ui-btn" data-variant="primary">
            Go to the course page
          </Link>
        )}
      </p>
    </main>
  );
}

/**
 * Everything else that can stop this page rendering.
 *
 * The tenancy refusal is the one worth calling out: the detail endpoints answer for any
 * course from any academy's hostname, so a course id from elsewhere loads at the API and
 * is refused here instead.
 */
function PlayerProblem({
  error,
  courseId,
  notOurs = false,
}: {
  error: unknown;
  courseId: string;
  notOurs?: boolean;
}) {
  const status = error instanceof ApiError ? error.status : 0;
  const missing = status === 404 || status === 400;

  return (
    <main className="player player--message">
      <h1>{missing || notOurs ? 'We could not find that course' : 'This course did not load'}</h1>
      <p>
        {notOurs
          ? 'It is not part of this academy. If someone sent you this link, they may have meant a different site.'
          : missing
            ? 'The link may be wrong, or the course may have been unpublished since it was shared with you.'
            : 'The academy did not answer. Nothing is wrong with your place in the course — please reload the page.'}
      </p>
      <p>
        {notOurs || missing ? (
          <Link to="/discover" className="ui-btn" data-variant="primary">
            Browse the catalogue
          </Link>
        ) : (
          <Link to={courseHref(courseId)} className="ui-btn" data-variant="primary">
            Back to the course page
          </Link>
        )}
      </p>
    </main>
  );
}
